import React, { useState } from 'react';

// contactEvents: array of { id, name, start_date, meeting_date, meeting_time } from the contact's events array
export default function InteractionFormModal({ onSave, onClose, contactEvents = [] }) {
  const [form, setForm] = useState({
    interaction_type: 'call',
    interaction_date: new Date().toISOString().slice(0, 16),
    summary: '',
    deal_reference: '',
    next_follow_up: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async () => {
    setError('');
    if (!form.summary.trim()) { setError('Summary is required.'); return; }
    setSaving(true);
    try {
      await onSave({
        interaction_type: form.interaction_type,
        interaction_date: new Date(form.interaction_date).toISOString(),
        summary: form.summary.trim(),
        deal_reference: form.deal_reference.trim() || null,
        next_follow_up: form.next_follow_up || null,
      });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  // Only show upcoming (future) events
  const today = new Date().toISOString().split('T')[0];
  const upcomingEvents = contactEvents.filter(e => e.start_date >= today);

  const formatTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':');
    const hour = parseInt(h);
    return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Log Interaction</h2>
          <button className="btn-icon" onClick={onClose} style={{ fontSize: '1.2rem' }}>✕</button>
        </div>

        {/* Event context banner */}
        {upcomingEvents.length > 0 && (
          <div style={{
            marginBottom: 16, padding: '10px 14px', borderRadius: 6,
            background: 'rgba(106,173,123,0.1)', border: '1px solid rgba(106,173,123,0.3)',
          }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--color-success)', marginBottom: 6 }}>
              Upcoming Events
            </div>
            {upcomingEvents.map(ev => {
              const daysAway = Math.round((new Date(ev.start_date + 'T00:00:00') - new Date()) / 86400000);
              return (
                <div key={ev.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: 3 }}>
                  <span style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{ev.name}</span>
                  <span style={{ color: 'var(--color-text-secondary)' }}>
                    {ev.meeting_date && ev.meeting_time
                      ? `Meeting ${formatTime(ev.meeting_time)}`
                      : 'No meeting'}
                    {' · '}
                    {daysAway === 0 ? 'Today' : daysAway === 1 ? 'Tomorrow' : `${daysAway}d away`}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {error && (
          <div style={{
            background: 'rgba(196,92,92,0.1)', border: '1px solid rgba(196,92,92,0.3)',
            borderRadius: 6, padding: '8px 12px',
            fontSize: '0.85rem', color: 'var(--color-danger)', marginBottom: 14,
          }}>{error}</div>
        )}

        {/* Type selector */}
        <div className="form-group">
          <label className="form-label">Type *</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {['call', 'email', 'meeting'].map(type => (
              <button
                key={type}
                className={`btn btn-sm ${form.interaction_type === type ? 'btn-primary' : ''}`}
                onClick={() => setForm(prev => ({ ...prev, interaction_type: type }))}
                type="button"
              >
                {type === 'call' ? '📞 Call' : type === 'email' ? '✉️ Email' : '🤝 Meeting'}
              </button>
            ))}
          </div>
        </div>

        {/* Date & time */}
        <div className="form-group">
          <label className="form-label">Date & Time *</label>
          <input className="form-input" type="datetime-local" name="interaction_date" value={form.interaction_date} onChange={handleChange} />
        </div>

        {/* Summary */}
        <div className="form-group">
          <label className="form-label">Summary *</label>
          <textarea
            className="form-textarea"
            name="summary"
            value={form.summary}
            onChange={handleChange}
            rows={4}
            placeholder="What was discussed? Key takeaways, next steps, commitments made…"
            autoFocus
          />
        </div>

        {/* Deal reference */}
        <div className="form-group">
          <label className="form-label">Deal Reference <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
          <input
            className="form-input"
            name="deal_reference"
            value={form.deal_reference}
            onChange={handleChange}
            placeholder="e.g. Oakwood Apartments — GA"
          />
        </div>

        {/* Set next follow-up */}
        <div className="form-group">
          <label className="form-label">Set Next Follow-up <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
          <input className="form-input" type="date" name="next_follow_up" value={form.next_follow_up} onChange={handleChange} />
          <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginTop: 3 }}>
            Updates this contact's follow-up reminder if set.
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : 'Log Interaction'}
          </button>
        </div>
      </div>
    </div>
  );
}
